import { Autocomplete, FormControlLabel, FormGroup, Grid, Switch, TextField, Typography } from "@mui/material";
import { useRecordContext } from "ra-core";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import * as Yup from 'yup';
import { SimpleOption } from "~/components/governance/search/AmbientFacilitiesContext";
import { useClassificationRuleAmbinetDataCotnext } from "~/components/securefiles/ClassificationRuleAmbientDataProvider";
import { RuleDisplayRoot } from "~/components/securefiles/RuleDisplayComponentsExperiment";
import { DatabasePreset } from "~/components/securefiles/ruleDisplayPresets/databasePreset";
import { RuleDisplayPresetContextProvider } from "~/components/securefiles/RuleDisplayPrimitives";
import { useLmGlobalDisabledMsg } from "~/contexts/LocalMessageHookProtocols/useLmGlobalDisabledMsg";
import { useLmStatesPropagate } from "~/contexts/LocalMessageHookProtocols/useLmStatesPropagate";
import { LocalMessageLocation } from "~/contexts/LocalMessagingRoot";
import { DataClassificationRuleDto, DataClassificationRuleInput } from "~/gen/api/encrypt";
import { SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { promiseWithResolvers } from "~/utils/promiseWithResolvers";



const validationSchema =
  Yup.object().shape({
    displayName: Yup.string().max(200, 'Rule Name must be at most 200 characters').required('Rule Name is required'),
    // classification: Yup.object().required('Target Classification is required'),
    priority: Yup.string().required('Priority is required'),
    comment: Yup.string().max(800, 'Description must be at most 800 characters'),    
  })

const clssRuleEditStatesInit = (dto: DataClassificationRuleDto|undefined, classifications: SecondaryClassificationResponseDto[]) => {
  const initialValues = {
    displayName: dto?.displayName ?? '',
    classification: 
      (classifications.find(c => c.secondaryClassificationId === dto?.targetClassification) ?? null 
        ) as SecondaryClassificationResponseDto|null|'',
    // targetClassification: dto.targetClassification ?? '',
    priority: dto?.priority ?? 1,
    comment: dto?.comment ?? '',
    active: dto?.activeRule ?? true,
  }
  return ({
    prevValue: initialValues,
    stage: {...initialValues},
    errors: {} as { [K in keyof(typeof initialValues)]+?: string|undefined },
    touched: {} as { [K in keyof(typeof initialValues)]+?: boolean|undefined },
    globalDisabled: false,
    composedRuleDirty: false,
  })
}

const editorDirty = (v: ReturnType<typeof clssRuleEditStatesInit>) => {
  return Object.keys(v.touched).length > 0 || v.composedRuleDirty
}

const DataClassificationRuleEdit: FC = () => {
  const record = useRecordContext()
  const dto: DataClassificationRuleDto|undefined = record as any
  const { classifications } = useClassificationRuleAmbinetDataCotnext()

  const [states, setStates] = useState(() => clssRuleEditStatesInit(dto, classifications))
  useEffect(() => {
    setStates(clssRuleEditStatesInit(dto, classifications))
  }, [dto, classifications])
  
  const {informDirty} = useLmStatesPropagate({
    key: 'editor',
    asUpperStage: {
      dirtyStateInformed: {
        handler(informDirty) {
          return (_, dirty) => {
            setStates(v => {
              v.composedRuleDirty = dirty
              informDirty(editorDirty(v))
              return v
            })
          }
        },
        deps: []
      }
    },
    asLowerStage: {
      collectStates: {
        handler(validateComposedRule) {
          return async () => {
            const [_, {composedRule}] = await Promise.all([
              validateForm().then(pass => pass ? Promise.resolve(pass) : Promise.reject()),
              validateComposedRule(['composedRule'])
            ])
            const qStates = promiseWithResolvers()
            setStates(v => {
              qStates.resolve(v)
              return v
            })
            const v1: typeof states = (await qStates.promise) as any
            
            const payload: DataClassificationRuleInput = {
              displayName: v1.stage.displayName,
              priority: v1.stage.priority,
              comment: v1.stage.comment,
              activeRule: v1.stage.active,
              targetClassification: v1.stage.classification ? v1.stage.classification.secondaryClassificationId : undefined,
              ruleDisplayTree: JSON.stringify(composedRule) 
            }
            return payload
          }
        },
        deps: []
      }
    }
  })

  useLmGlobalDisabledMsg({
    listener(disabled) {
      setStates(v => 
        v.globalDisabled !== disabled ? {...v, globalDisabled: disabled} : v
      )
    }
  })

  const getEffectClssItem = () : SecondaryClassificationResponseDto|'' => {
    return states.stage.classification ?? ''
  }
  const setClssItem = (c: SecondaryClassificationResponseDto|'') => {
    setStates(v => {
      v.stage.classification = c
      if (v.prevValue.classification !== c) {
        v.touched.classification = true
      } else {
        delete v.touched.classification
      }
      v.errors = validate(v.stage) ?? {}
      informDirty(editorDirty(v))
      // if (!c) {
      //   v.errors.classification = 'Target Classification is required'
      // } else {
      //   delete v.errors.classification
      // }
      return {...v}
    })
  }
  const setActive = (a: boolean) => {
    setStates(v => {
      v.stage.active = a
      if (v.prevValue.active !== v.stage.active) {
        v.touched.active = true
      } else {
        delete v.touched.active
      }
      informDirty(editorDirty(v))
      return {...v}
    })
  }
  
  const validate = (x: any) => {
    try {
      validationSchema.validateSync(x, {abortEarly:false})
    } catch (e: any) {
      if (e.name === 'ValidationError') {
        const errors = {} as Record<string, string>
        for (let err of e.inner) {
          errors[err.path] = err.message
        }
        return errors
      }
    }
  }
  const validateForm = () => {
    return new Promise<boolean>(resolve => {
      setStates(v => {
        v.errors = validate(v.stage) ?? {}
        if (!v.stage.classification) { v.errors['classification'] = 'Target Classification is required' }
        v.touched = Object.keys(v.prevValue).reduce((acc, k) => {
          acc[k] = true
          return acc
        }, {} as any)
        resolve(Object.values(v.errors).every(x => !x))
        return {...v}
      })
    })
  }
  const timerRef = useRef({} as any)
  const fieldProps = (path: keyof typeof states.stage, transform?: (x:any)=>any) => ({
    value: states.stage[path],
    onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (timerRef.current[path]) {
        window.clearTimeout(timerRef.current[path])
        delete timerRef.current[path]
      }
      const value = transform ? transform(e.target.value) : e.target.value;
      const setValue = (newValue: any) => {
        setStates(v => {
          (v.stage as any)[path] = newValue
          if (v.prevValue[path] !== newValue) {
            v.touched[path] = true
          } else {
            delete v.touched[path]
          }
          v.errors = validate(v.stage) ?? {}
          informDirty(editorDirty(v))
          return {...v}
        })
      }
      setValue(value)
      if (typeof value === 'string' && value.trim() !== value) {
        timerRef.current[path] = setTimeout(() => setValue(value.trim()), 1500)
      }
    },
    error: Boolean(states.touched[path] && states.errors[path]),
    helperText: states.touched[path] ? states.errors[path] : undefined,
  })

  return (
    <>
      <Grid container sx={{ py: 2, }} spacing={2} >
        <Grid item xs={7} container spacing={2} >
          <Grid item xs={5}>
            <TextField label="Rule Name*" fullWidth disabled={states.globalDisabled}
              {...fieldProps('displayName')}
            />
          </Grid>
          <Grid item xs={7}>
            <Autocomplete disableClearable={true} disabled={states.globalDisabled}
              options={classifications}
              getOptionLabel={(option) => (option.text || option) as string}
              renderInput={(params) => 
                <TextField label="Target Classification*" {...params} fullWidth 
                  error={Boolean(states.touched.classification && states.errors.classification)}
                  helperText={states.touched.classification ? states.errors.classification : undefined}
                />
              } 
              value={getEffectClssItem() as SimpleOption}
              onChange={(event, value, reason) => {
                if (reason === 'selectOption') {
                  if ((value as any)?.value === null) { // choosed '[None]'
                    setClssItem('')
                  } else {
                    const cls1 = value as SecondaryClassificationResponseDto
                    setClssItem(cls1)
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField label="Priority*" sx={{ width: 150, mr: 12 }} type="number" disabled={states.globalDisabled}
              {...fieldProps('priority', (x: any) => {
                let n = +x
                if (Number.isNaN(n)) { return 1 }
                n = !Number.isInteger ? Math.round(n) : n
                return (n < 1 ? 1 
                  : n > 999999999 ? 999999999
                  : n)
              })} />
          </Grid>
          <Grid item xs={7}>
            <FormGroup>
              <FormControlLabel disabled={states.globalDisabled}
                control={<Switch color="success" checked={states.stage.active} 
                  onChange={(e) => setActive(e.target.checked)} />}
                label="Active"
              />
            </FormGroup>
          </Grid>
        </Grid>
        <Grid item xs={5}>
          <TextField label="Description" multiline fullWidth disabled={states.globalDisabled}
            {...fieldProps('comment')}
          />
        </Grid>
      </Grid>
      <Typography variant="subtitle2">Rule Definition</Typography>
      <RuleDisplayPresetContextProvider value={DatabasePreset}>
        <LocalMessageLocation id="composedRule">
          <RuleDisplayRoot globalDisabled={states.globalDisabled} />
        </LocalMessageLocation>
      </RuleDisplayPresetContextProvider>
    </>
  )
}

export default DataClassificationRuleEdit;
